import React, { useState } from "react";
import { Form, Input, Button, Typography, message } from "antd";
import { Link, useNavigate } from "react-router-dom";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import authService from "../services/authService";

const { Title, Text } = Typography;

const RegisterForm = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await authService.registerUser(values);

      if (response?.checkoutUrl) {
        message.success("Details saved. Redirecting to payment...");
        window.location.assign(response.checkoutUrl);
        return;
      }

      message.success("Registration successful! Continue to payment.");
      navigate("/pricing");
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        "Network error or server not responding";
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* --- Left Side --- */}
      <div className="hidden md:flex md:w-1/2 bg-[#1e5782] text-white items-center justify-center flex-col p-10">
        <h1 className="text-3xl font-semibold mb-4 text-center leading-snug">
          Empowering you to help your customers
        </h1>
        <p className="text-gray-200 text-center max-w-md text-sm leading-relaxed">
          Discover a smarter way to help your customers decide which supplements are right for them.
        </p>
        <div className="mt-8 w-16 h-[2px] bg-white/30 rounded-full"></div>
      </div>

      {/* --- Right Side (Form, no card) --- */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-white px-6 py-16">
        <div className="w-full max-w-md">
          {/* Logo (for mobile view) */}
          <div className="md:hidden flex justify-center mb-6">
            <img
              src="/assets/logo.png"
              alt="Company Logo"
              className="w-36 h-12 object-contain"
            />
          </div>

          <Title level={3} className="text-center mb-8 text-gray-800">
            Create your account
          </Title>

          <Form
            layout="vertical"
            form={form}
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <Form.Item
              label="Full Name"
              name="fullName"
              rules={[{ required: true, message: "Please enter your full name" }]}
            >
              <Input prefix={<UserOutlined />} placeholder="John Doe" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="john@example.com" />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="phone"
              rules={[{ required: true, message: "Please enter your phone number" }]}
            >
              <Input prefix={<PhoneOutlined />} placeholder="+1 (555) 123-4567" />
            </Form.Item>

            <Form.Item
              label="Username"
              name="username"
              rules={[{ required: true, message: "Please enter your username" }]}
            >
              <Input prefix={<UserOutlined />} placeholder="johndoe" />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: "Please enter your password" }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="••••••••" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="w-full bg-[#1e5782] hover:bg-[#184766] flex items-center justify-center mt-4"
                size="large"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex space-x-1">
                    <span className="dot w-2 h-2 bg-white rounded-full animate-bounce [animation-delay:-0.3s]" />
                    <span className="dot w-2 h-2 bg-white rounded-full animate-bounce [animation-delay:-0.15s]" />
                    <span className="dot w-2 h-2 bg-white rounded-full animate-bounce" />
                  </span>
                ) : (
                  "Create Account"
                )}
              </Button>
            </Form.Item>

            <Text className="block text-center text-gray-600 mt-4">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-[#1e5782] font-medium hover:underline"
              >
                Sign in
              </Link>
            </Text>
            <Text className="block text-center text-gray-500 mt-3 text-xs">
              By creating an account, you agree to our{" "}
              <Link to="/privacy-policy" className="text-[#1e5782] hover:underline">
                Privacy Policy
              </Link>
              {" "}and{" "}
              <Link to="/terms-of-use" className="text-[#1e5782] hover:underline">
                Terms of Use
              </Link>
              .
            </Text>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
